import Alert from '@base/components/src/Alert';
import Link from '@base/components/src/Link';
import useShoppingCart from '@package/hooks/src/useShoppingCart';
import config from 'config-base';
import { useEffect, useState } from 'react';

import Addons from './CartProductAddons';
import styles from './CartProductList.module.css';
import CartProductRow from './CartProductRow';
import ProductService from './ProductService';
import SuggestedProducts from './SuggestedProducts';
import { apiClient } from '../../services';
import AlertsApiService from '@package/core/src/alerts/AlertsApiService';
import { AlertProps, CartProductListProps } from './types';
import { SubOrder } from '@package/core/src/shoppingCart/types';

const alertsService = new AlertsApiService(apiClient);

export default function CartProductList(props: Readonly<CartProductListProps>) {
  const { subOrders, readOnly = false, checkoutButtonAvailable, regularCouponApplied } = props;
  const {
    saveToWishlist,
    addProductService,
    deleteProduct,
    updateProductQuantity,
    addProduct,
    deleteProductService,
    applyCouponToItem,
    loading,
    success,
  } = useShoppingCart({ disableGetShoppingCart: readOnly, mainCart: true });

  const [alertState, setAlertState] = useState<AlertProps>({
    processing: false,
    finished: false,
    processMessage: '',
  });

  let sellers: any = [];

  const setSellers = (vendorOrderId: number, prevSellers: any) => (sellers = [vendorOrderId, ...prevSellers]);

  const updateCartAlertMessage = (message: string) => setAlertState({ processMessage: message });

  const onGetTermsContent = async () => {
    const termsContent = await alertsService.getTermsAlert();
    return termsContent?.data;
  };

  useEffect(() => {
    setAlertState({
      processing: loading,
      finished: loading ? false : success,
      processMessage: alertState.processMessage,
    });
  }, [loading, success]);

  return (
    <>
      {subOrders?.map((order: SubOrder) =>
        order?.products?.map((product) => (
          <div className={styles.subOrderContainer} key={product.seq}>
            <CartProductRow
              dataTestId={`${product.id}-product-item`}
              orderId={order.orderId}
              product={product}
              vendorName={order.vendor}
              readOnly={readOnly}
              regularCouponApplied={regularCouponApplied}
              deleteProduct={async (data) => {
                updateCartAlertMessage('Removing item');
                await deleteProduct(data);
              }}
              updateProductQuantity={async (data) => {
                updateCartAlertMessage('Update quantity');
                await updateProductQuantity(data);
              }}
              onApplyCouponToItem={async (couponCode, isApply) => {
                updateCartAlertMessage('Applying coupon to item');
                await applyCouponToItem({ couponCode, isApply });
              }}
              onGetTermsContent={onGetTermsContent}
            />
            {product.services &&
              !readOnly &&
              product.services.map((service) => (
                <ProductService
                  key={`${service.name}${product.quantity}`}
                  data={service}
                  contentClassName={styles.servicesExpert}
                  productSku={product.id}
                  deleteSpecifiedService={async (sku:string)=>{
                    updateCartAlertMessage('Removing item.');
                    const productToDelete = order.products?.find((product) => product.id === sku);
                    if(productToDelete){
                      await deleteProduct({
                        productItemId: productToDelete.seq,
                        orderId: order.orderId,
                        product,
                      });
                    }
                  }}
                  addProductService={async (data) => {
                    updateCartAlertMessage('Adding service to order');
                    await addProductService({
                      ...data,
                      productQuantity: product.quantity,
                    });
                  }}
                  deleteProductService={async () => {
                    updateCartAlertMessage('Removing item.');
                    await deleteProductService({
                      orderId: order.orderId,
                      productSku: product.id,
                      serviceType: service.name,
                    });
                  }}
                />
              ))}
            {!readOnly && (
              <Addons
                relatedProductsAvailable={!!product.relatedProductsAvailable}
                productSku={product.id}
                productQuantity={product.quantity}
                addProduct={async (data) => {
                  updateCartAlertMessage('Adding item to order');
                  await addProduct(data);
                }}
                addProductService={async (data) => {
                  updateCartAlertMessage('Adding service to order');
                  await addProductService(data);
                }}
                deleteProductService={async (serviceType) => {
                  updateCartAlertMessage('Removing item.');
                  await deleteProductService({
                    orderId: order.orderId,
                    productSku: product.id,
                    serviceType,
                  });
                }}
              />
            )}

            {Boolean(order.vendorShipping.freeShippingMessage) &&
              Boolean(order.vendorShipping.freeShippingAmountLeft) &&
              !readOnly &&
              !sellers.includes(order.vendorId) && (
                <>
                  <div style={{ display: 'none' }}>{setSellers(order.vendorId, sellers)}</div>
                  <SuggestedProducts
                    vendorId={order.vendorId}
                    vendorName={order.vendor}
                    minPrice={order.vendorShipping.freeShippingAmountLeft || 0}
                    products={order.products || []}
                    addProduct={async (data) => {
                      updateCartAlertMessage('Adding item to order');
                      await addProduct(data);
                    }}
                  />
                </>
              )}
          </div>
        )),
      )}

      {!readOnly && (
        <div className={`${styles.linkContainer} linkContainer`}>
          <span
            id="save-to-wish-list"
            className="!font-bold cursor-pointer"
            onClick={async () => await saveToWishlist()}
          >
            Save cart to wishlist
          </span>

          <Link
            classes={`${styles.cartLink} continueShoppingBtnCart`}
            showCursorPointer
            href={config.mainPyramydairUrl}
            data-testid="continue-shipping-btn"
          >
            <span className={'text-sm'}>
              Continue shopping <span className="!text-[8px] inline-block rotate-45">&#9701;</span>
            </span>
          </Link>
        </div>
      )}

      <Alert
        processing={alertState.processing}
        finished={alertState.finished}
        processMessage={alertState.processMessage}
        finishMessage={'Order updated.'}
        checkoutButtonAvailable={checkoutButtonAvailable}
      />
    </>
  );
}
